import { useState } from 'react';
import { RiAddLine, RiEyeLine, RiDeleteBinLine } from 'react-icons/ri';
import GlassCard from '../ui/v2/GlassCard';
import type { Resume } from '../../models/v2/profile';
import UploadResumeModal from '../profile/UploadResumeModal';
import ResumePreviewModal from '../profile/ResumePreviewModal';
import { useProfileMutation, useProfile } from '../../context/ProfileContext';
import { resumeService } from '../../services/resumeService';

interface ResumeSelectionProps {
    resumes: Resume[];
    selectedId: string;
    onSelect: (id: string) => void;
}

export default function ResumeSelection({ resumes, selectedId, onSelect }: ResumeSelectionProps) {
    // Modal State
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Mutation for adding resume
    const { mutate: uploadResume } = useProfileMutation<File>();
    // Mutation for deleting resume
    const { mutate: deleteResume, isMutating: isDeleting } = useProfileMutation<string>();

    const { updateLocalState } = useProfile();

    const handleUpload = async (file: File) => {
        await uploadResume(file, {
            mutationFn: (f) => resumeService.upload(f, undefined, resumes.length === 0),
            successMessage: "Resume uploaded successfully!",
            onSuccess: (newResume: Resume) => {
                // Optimistic update
                updateLocalState(prev => {
                    if (!prev) return prev;
                    return { ...prev, resumes: [...prev.resumes, newResume] };
                });

                setIsUploadOpen(false);
                if (newResume?.id) {
                    onSelect(newResume.id);
                }
            }
        });
    };

    const handleDelete = async (e: React.MouseEvent, resumeId: string) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to delete this resume? This action cannot be undone.")) return;

        await deleteResume(resumeId, {
            mutationFn: (id) => resumeService.deleteResume(id),
            successMessage: "Resume deleted successfully",
            onSuccess: () => {
                // Optimistic update
                updateLocalState(prev => {
                    if (!prev) return prev;
                    return { ...prev, resumes: prev.resumes.filter(r => r.id !== resumeId) };
                });

                if (selectedId === resumeId) {
                    onSelect('');
                }
            }
        });
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">Select Resume</h2>
                <button
                    onClick={() => setIsUploadOpen(true)}
                    className="flex items-center gap-1.5 text-sm md:text-base font-medium text-gray-400 hover:text-white transition-colors"
                >
                    <RiAddLine size={20} />
                    <span>Add New Resume</span>
                </button>
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pr-2">
                {resumes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 border border-white/5 bg-white/5 rounded-xl text-center p-6">
                        <p className="text-gray-400 mb-4">No resumes found. partial profile data?</p>
                        <button
                            onClick={() => setIsUploadOpen(true)}
                            className="bg-white hover:bg-gray-200 text-black px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Upload Resume
                        </button>
                    </div>
                ) : (
                    resumes.map((resume) => {
                        const isSelected = selectedId === resume.id;

                        return (
                            <div
                                key={resume.id}
                                onClick={() => onSelect(resume.id)}
                                className="relative group cursor-pointer"
                            >
                                <GlassCard
                                    className={`
                                        !p-4 transition-all duration-300 border
                                        ${isSelected
                                            ? 'border-white/40 bg-white/10 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
                                            : 'border-white/5 hover:border-white/10 hover:bg-white/5'
                                        }
                                    `}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-4">
                                            {/* Selection Radio / Indicator */}
                                            <div className={`
                                                w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                                                ${isSelected
                                                    ? 'border-white'
                                                    : 'border-gray-500 group-hover:border-gray-400'
                                                }
                                            `}>
                                                {isSelected && (
                                                    <div className="w-2.5 h-2.5 rounded-full bg-white" />
                                                )}
                                            </div>

                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className={`font-semibold text-base ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                                                        {resume.name}
                                                    </h3>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Uploaded: {resume.created_at ? new Date(resume.created_at).toLocaleDateString() : 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPreviewUrl(resume.file_url);
                                                }}
                                                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                                                title="Preview"
                                            >
                                                <RiEyeLine size={18} />
                                            </button>

                                            <button
                                                onClick={(e) => handleDelete(e, resume.id)}
                                                className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-white/10 transition-colors"
                                                title="Delete"
                                                disabled={isDeleting}
                                            >
                                                <RiDeleteBinLine size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </GlassCard>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Modals */}
            <UploadResumeModal
                isOpen={isUploadOpen}
                onClose={() => setIsUploadOpen(false)}
                onUpload={handleUpload}
                isFirstResume={resumes.length === 0}
            />

            <ResumePreviewModal
                url={previewUrl}
                isOpen={!!previewUrl}
                onClose={() => setPreviewUrl(null)}
            />
        </div>
    );
}
